import { FREE_TIP_ID } from '../data/examTipsData';
import { readLastPracticeTrack, readStoredValue } from './practiceContext';

export const LANGUAGES = ['fr', 'de'];
export const CHAPTER_TRACKS = ['dialogue', 'discussion'];

export const normalizeLanguage = (lang) => (lang === 'de' ? 'de' : 'fr');
export const normalizeChapterTrack = (track) => (track === 'discussion' ? 'discussion' : 'dialogue');

export const buildExamOverviewPath = (lang = 'fr') => `/${normalizeLanguage(lang)}/fide-exam-overview`;

/** The two exam types the Mock Exams page switches between. */
export const MOCK_EXAM_TYPES = ['oral', 'listening'];

export const normalizeMockExamType = (examType) => (examType === 'listening' ? 'listening' : 'oral');

/**
 * `/{lang}/mock-exams` for the oral exam, `/{lang}/mock-exams/listening` for listening.
 * Oral keeps the bare path so every existing link, the canonical URL and the sitemap
 * entry stay exactly as they were; the tab is in the URL so Back returns to the tab
 * the candidate came from and either tab can be linked or bookmarked directly.
 *
 * The bare oral path carries no explicit type, so MockExams.jsx treats it as "no
 * preference" and can redirect it to whichever tab the candidate used last. Pass
 * `explicit: true` for a link whose whole point is "go to the oral exam, no matter
 * what" (a labelled oral/listening choice) — it adds a real `/oral` segment so the
 * intent survives that redirect.
 */
export const buildMockExamsPath = (lang = 'fr', examType = 'oral', { explicit = false } = {}) => {
  const base = `/${normalizeLanguage(lang)}/mock-exams`;
  const normalizedType = normalizeMockExamType(examType);
  if (normalizedType === 'listening') return `${base}/listening`;
  return explicit ? `${base}/oral` : base;
};

export const buildExamTipsBasePath = (lang = 'fr') => `/${normalizeLanguage(lang)}/exam-tips`;

export const buildExamTipPath = (lang = 'fr', tipId = FREE_TIP_ID) => `${buildExamTipsBasePath(lang)}/${tipId}`;

export const buildTopicsBasePath = (lang = 'fr') => `/${normalizeLanguage(lang)}/fide-test-topics`;

export const buildTopicsDiscussionPath = (lang = 'fr') => `/${normalizeLanguage(lang)}/fide-test-b1-discussion`;

export const buildMaterialLandingPath = (lang = 'fr', material = 'dialogue') => {
  const normalized = normalizeLanguage(lang);
  if (material === 'discussion') return buildTopicsDiscussionPath(normalized);
  if (material === 'vocab') return `/${normalized}/fide-vocabulary`;
  if (material === 'flashcards') return `/${normalized}/fide-flashcards`;
  return buildTopicsBasePath(normalized);
};

export const buildTopicsVideoPath = (lang = 'fr', scenarioId) => `${buildTopicsBasePath(lang)}/video/${scenarioId}`;

/**
 * The URL segment the whole practice tree hangs off. Every practice path is built
 * from it and every pathname matcher below interpolates it, so the segment can be
 * renamed in one place. The previous `/{lang}/practice` spelling never shipped, so it
 * needs no redirect.
 */
export const PRACTICE_SEGMENT = 'fide-practice';

export const buildPracticePath = (lang = 'fr') => `/${normalizeLanguage(lang)}/${PRACTICE_SEGMENT}`;

export const buildPracticeSectionPath = (lang = 'fr', level = 'a2', section = 'intro') =>
  `${buildPracticePath(lang)}/${level}/${section}`;

/** The views a catalog topic (and a guided section) can be seen through. */
export const PRACTICE_TOPIC_VIEWS = ['dialogue', 'discussion', 'vocab', 'flashcards'];

/**
 * The clean, topic-centric URL used when browsing from the Topic catalog:
 * `/{lang}/fide-practice/topics/{id}/{view}`. Level/track is intentionally absent —
 * a topic is one entity and the view is a suffix.
 */
export const buildPracticeTopicPath = (lang = 'fr', topicId, view = 'dialogue') =>
  `${buildPracticePath(lang)}/topics/${topicId}/${view}`;

/** The full Vocabulary/Flashcards libraries live in the topic practice tree. */
export const buildPracticeAllMaterialPath = (lang = 'fr', view = 'vocab') =>
  buildPracticeTopicPath(lang, 'all', view === 'flashcards' ? 'flashcards' : 'vocab');

/** A guided section's own Vocabulary/Flashcards view: `/{lang}/fide-practice/{level}/{section}/{view}`. */
export const buildPracticeSectionViewPath = (lang = 'fr', level = 'a2', section = 'intro', view = 'vocab') =>
  `${buildPracticeSectionPath(lang, level, section)}/${view}`;

/**
 * The main-vocab topics that belong to a practice section rather than to a
 * catalog topic. Their Vocabulary/Flashcards pages are reached through the
 * chapter routes, so the tri-tab's "Topics" link has to come back here instead
 * of to a catalog topic that doesn't exist.
 */
export const PRACTICE_VOCAB_TOPIC_SECTIONS = {
  17: ['a2', 'intro'], // Se présenter
  18: ['a2', 'image'], // Décrire une image
  19: ['a2', 'telephone-roleplay'], // Téléphoner
};

export const getPracticeSectionForVocabTopic = (topicId) => PRACTICE_VOCAB_TOPIC_SECTIONS[Number(topicId)] ?? null;

export const buildPracticeSectionPathForVocabTopic = (lang = 'fr', topicId) => {
  const match = getPracticeSectionForVocabTopic(topicId);
  return match ? buildPracticeSectionPath(lang, match[0], match[1]) : null;
};

/**
 * A1's section is one page per situation: `/{lang}/fide-practice/a1/dialogue/{id}`.
 * The bare section URL is situation 1, so both shapes are valid links.
 */
export const buildA1SituationPath = (lang = 'fr', situationId = 1) =>
  `${buildPracticeSectionPath(lang, 'a1', 'dialogue')}/${situationId}`;

const A1_SITUATION_PATTERN = new RegExp(`/${PRACTICE_SEGMENT}/a1/dialogue/(\\d+)(?:/|$)`);

export const getA1SituationIdFromPathname = (pathname = '') => pathname.match(A1_SITUATION_PATTERN)?.[1] ?? null;

/** Identifies guided-section Vocabulary/Flashcards URLs (topics 17–19). */
export const getPracticeSectionForVocabPathname = (pathname = '') => {
  const topicId = pathname.match(/\/(?:vocab|flashcards)\/(\d+)(?:\/|$)/)?.[1];
  return topicId ? getPracticeSectionForVocabTopic(topicId) : null;
};

/**
 * The catalogs now live inside the practice journey: the dialogue catalog under
 * A2 › Telephone Dialogue, the discussion catalog under B1 › Discussion. Every
 * other chapter builder composes from this, so re-pointing it moves the whole
 * family (and all ~14 callers) at once.
 */
export const buildChapterBasePath = (lang = 'fr', track = 'dialogue') =>
  normalizeChapterTrack(track) === 'discussion'
    ? buildPracticeSectionPath(lang, 'b1', 'discussion')
    : buildPracticeSectionPath(lang, 'a2', 'telephone');

/** The pre-journey URLs, kept only so the legacy redirect shims can target them. */
export const buildLegacyChapterBasePath = (lang = 'fr', track = 'dialogue') =>
  `/${normalizeLanguage(lang)}/${normalizeChapterTrack(track)}`;

export const buildChapterExamplesPath = (lang = 'fr', track = 'dialogue') => buildChapterBasePath(lang, track);

export const buildChapterTopicsPath = (lang = 'fr', track = 'dialogue') =>
  `${buildChapterBasePath(lang, track)}/topics`;

export const buildChapterTopicPath = (lang = 'fr', track = 'dialogue', topicId) =>
  `${buildChapterTopicsPath(lang, track)}/${topicId}`;

export const buildChapterVideoPath = (lang = 'fr', track = 'dialogue', scenarioId) =>
  `${buildChapterBasePath(lang, track)}/video/${scenarioId}`;

export const buildChapterVocabPath = (lang = 'fr', track = 'dialogue', topicId) =>
  `${buildChapterBasePath(lang, track)}/vocab/${topicId}`;

export const buildChapterFlashcardsPath = (lang = 'fr', track = 'dialogue', topicId) =>
  `${buildChapterBasePath(lang, track)}/flashcards/${topicId}`;

/**
 * A catalog topic's link for a given view, aware of which sidebar rail you're in.
 * From the Topic catalog (`fromLevel` false) it uses the clean topic shape; from
 * the Level rail (B1 → Discussion) it keeps the level/track shape so the sidebar
 * stays on the level rail. The URL shape — not a query marker — is what
 * distinguishes the two modes.
 */
export const buildTopicViewLink = (lang = 'fr', topicId, view = 'dialogue', { fromLevel = false } = {}) => {
  if (!fromLevel || topicId === 'other') return buildPracticeTopicPath(lang, topicId, view);
  // Level mode today is only B1 → Discussion; dialogue never appears there.
  const track = view === 'dialogue' ? 'dialogue' : 'discussion';
  if (view === 'vocab') return buildChapterVocabPath(lang, track, topicId);
  if (view === 'flashcards') return buildChapterFlashcardsPath(lang, track, topicId);
  return buildChapterTopicPath(lang, track, topicId);
};

const TOPIC_TRACK_PATTERN = new RegExp(`^/(?:fr|de)/${PRACTICE_SEGMENT}/topics/\\d+/(dialogue|discussion)(?=/|$)`, 'i');

const SECTION_TRACK_PATTERN = new RegExp(`^/(?:fr|de)/${PRACTICE_SEGMENT}/(?:a2/telephone|b1/discussion)(?=/|$)`, 'i');

/**
 * Resolves the chapter track from a URL. Matches the journey paths
 * (`/fr/fide-practice/a2/telephone/...` -> dialogue,
 * `/fr/fide-practice/b1/discussion/...` -> discussion) as well as the pre-journey
 * `/fr/dialogue/...` shape, which the redirect shims still render for one tick
 * before navigating away.
 */
export const getChapterTrackFromPathname = (pathname = '') => {
  // Topic-centric shape: `/{lang}/fide-practice/topics/{id}/dialogue|discussion`.
  const topicView = pathname.match(TOPIC_TRACK_PATTERN);
  if (topicView) return topicView[1].toLowerCase();
  const practice = pathname.match(SECTION_TRACK_PATTERN);
  if (practice) {
    return /b1\/discussion/i.test(practice[0]) ? 'discussion' : 'dialogue';
  }
  return pathname.match(/^\/(?:fr|de)\/(dialogue|discussion)(?=\/|$)/i)?.[1]?.toLowerCase() ?? null;
};

const TOPIC_CATALOG_PATTERN = new RegExp(
  `^/(?:fr|de)/${PRACTICE_SEGMENT}/topics/(?:\\d+|other)/(?:dialogue|discussion|vocab|flashcards)(?=/|$)`,
  'i',
);

const LEVEL_TOPIC_PATTERN = new RegExp(
  `^/(?:fr|de)/${PRACTICE_SEGMENT}/(?:a2/telephone|b1/discussion)/(?:topics|vocab|flashcards)/\\d+(?=/|$)`,
  'i',
);

/**
 * True for the Topic catalog's own URL shape, `/{lang}/fide-practice/topics/{id}/{view}`.
 * These pages get their material tabs from the practice sidebar's 2×2 picker, so
 * they must not also draw the in-content tab bar — that was the duplication the
 * sidebar picker replaced. The Level rail's pages keep theirs.
 */
export const isTopicCatalogPath = (pathname = '') => TOPIC_CATALOG_PATTERN.test(pathname);

/**
 * True for the Level rail's URL shape — the level/track topic form
 * `/{lang}/fide-practice/{a2/telephone|b1/discussion}/(topics|vocab|flashcards)/{id}`.
 * The clean `…/topics/{id}/{view}` shape is topic mode and returns false.
 * Replaces the old `?from=level` marker: the shape alone says which rail you're in.
 */
export const isLevelTopicPath = (pathname = '') => LEVEL_TOPIC_PATTERN.test(pathname);

export const CHAPTERS_TRACK_STORAGE_KEY = 'fide-test-chapters-track';

/**
 * Which catalog a track-less entry point opens (`/fide-test-examples`, and language
 * switching onto it). This is the same question `practice:lastView` answers, so it
 * reads that first and keeps the older key as the fallback — for sessions that have
 * only been through the pages which still write it (Chapters' `?notexplicit=1` path).
 */
export const resolveStoredChapterTrack = () => {
  if (typeof window === 'undefined') return 'dialogue';
  const lastTrack = readLastPracticeTrack();
  if (lastTrack) return lastTrack;
  return readStoredValue(CHAPTERS_TRACK_STORAGE_KEY) === 'A2-B1' ? 'discussion' : 'dialogue';
};

export const isChapterScopedPath = (pathname = '') => Boolean(getChapterTrackFromPathname(pathname));

export const buildExampleVideosPath = () => '/fr/fide-test-example-videos';

export const buildExampleVideoPath = (youtubeId) => `${buildExampleVideosPath()}/${youtubeId}`;

export const getPathnameWithoutLanguage = (pathname = '') => pathname.replace(/^\/(fr|de)(?=\/|$)/i, '') || '/';
